import { Send } from "lucide-react";
import type React from "react";

interface ChatInputProps {
	value: string;
	onChange: (value: string) => void;
	onSubmit: (event: React.FormEvent) => void;
	selectedThreadId: string | null;
	sendIsPending: boolean;
	selectedDocumentIds?: string[];
}

export const ChatInput: React.FC<ChatInputProps> = ({
	value,
	onChange,
	onSubmit,
	selectedThreadId,
	sendIsPending,
	selectedDocumentIds = [],
}) => {
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(event.target.value);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		// Prevent graph canvas / global keyboard handlers from reacting while typing in the chat box
		if (
			event.key === "ArrowUp" ||
			event.key === "ArrowDown" ||
			event.key === "ArrowLeft" ||
			event.key === "ArrowRight"
		) {
			event.stopPropagation();
		}

		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			onSubmit(event as unknown as React.FormEvent);
		}
	};

	const placeholder = !selectedThreadId ? "Start typing to create a thread..." : "Start typing...";

	const sourceCount = selectedDocumentIds.length;
	const sourceLabel = sourceCount === 1 ? "1 source" : sourceCount > 1 ? `${sourceCount} sources` : null;

	// Allow starting a conversation without explicitly creating/selecting a thread first.
	const isDisabled = sendIsPending;
	const isSendDisabled = !value.trim() || sendIsPending;

	return (
		<form onSubmit={onSubmit} className="chat-input-container">
			<div className="chat-input-inner">
				<input
					type="text"
					value={value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={isDisabled}
					className="chat-input-field"
				/>
				{sourceLabel && <span className="chat-input-source-count">{sourceLabel}</span>}
				<button type="submit" disabled={isSendDisabled} className="chat-send-button" aria-label="Send message">
					<Send className="h-4 w-4" />
				</button>
			</div>
		</form>
	);
};
